import { createAction } from '@reduxjs/toolkit';
import { notAvailable } from 'core/common/constants';
import { normalizeError } from 'core/common/errors';
import { ATTRS_ANALYTICS_FUNNEL_NAME, SpanName } from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store';
import { getFunnelName } from 'core/funnel/store/getFunnelName';
import { getUserLocation } from 'core/user/store/selectors';
import { PaymentError, ResignFormData } from '../../entities';

export const initializeResignFormPending = createAction(`payments/INITIALIZE_RESIGN_FORM/pending`);

export const initializeResignFormSucceed = createAction<ResignFormData>(
  `payments/INITIALIZE_RESIGN_FORM/succeed`,
);

export const initializeResignFormFailed = createAction<PaymentError>(
  `payments/INITIALIZE_RESIGN_FORM/failed`,
);

export const initializeResignFormRequest =
  (offerId: string, tokenId: string): AppThunk =>
  async (dispatch, getState, context) => {
    const {
      purchaseService,
      loggingService: logger,
      observabilitySystem,
      analyticsService,
      errorMonitoringService,
    } = context;

    const userLocation = getUserLocation(getState());
    const funnelName = getFunnelName(getState());

    dispatch(initializeResignFormPending());

    const spanAttributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
    };

    const span = observabilitySystem.startSpan(SpanName.INITIALIZE_RESIGN_FORM);
    span.setAttributes(spanAttributes);
    span.addEvent('Pending');

    try {
      const resignFormData = await purchaseService.getResignForm(offerId, tokenId);

      dispatch(initializeResignFormSucceed(resignFormData));
      analyticsService.trackEvent('initialize_resign_form_succeed');

      span.addEvent('Succeed');
      span.succeed();
    } catch (err) {
      const error = normalizeError(err);
      logger.error(error);

      dispatch(initializeResignFormFailed(error));
      analyticsService.trackEvent('initialize_resign_form_failed', {
        offerId,
        message: error.message,
      });

      span.addEvent('Failed');
      span.fail(error);

      errorMonitoringService.captureError(error, {
        tags: {
          offer_id: offerId,
          token_id: tokenId,
          get_params: location.search || notAvailable,
          error_message: error.message,
          error_reason: 'initialize_resign_form_failed',
          funnel_name: funnelName,
          ...userLocation,
        },
      });
    }
  };

export const resignFormSucceed = createAction(`payments/RESIGN_FORM/succeed`);

export const resignFormFailed = createAction(`payments/RESIGN_FORM/failed`);
